import { memo, type ReactNode } from 'react';

import classNames from 'classnames';

import bingoGlow from '../../../../../../assets/bingo-glow.png';
import bingoThumbnail from '../../../../../../assets/bingo-thumbnail.png';
import dollarSign from '../../../../../../assets/dollar-sign.svg';
import fallbackThumbnail from '../../../../../../assets/thumbnails/game_thumbnail_seven.png';
import { SocialCoin } from '../../../../../../components/SocialCoin/SocialCoin';
import { SweepCoin } from '../../../../../../components/SweepCoin/SweepCoin';
import { useFitText } from '../../../../../../hooks/useFitText';
import type { WinMode, WinnerTileVM } from '../../types';

import styles from './WinnerTile.module.scss';

interface WinnerTileProps {
  tile: WinnerTileVM;
  amountMaxWidth: number;
  amountMaxFont: number;
  onSelect?: (gameId: number) => void;
}

// Coin icon + amount color per win variant ('bingo' wins override the SC/CC mode).
type WinVariant = 'bingo' | WinMode;
const WIN_VARIANTS: Record<WinVariant, { coin: ReactNode; amountClass: string }> = {
  bingo: {
    coin: <img alt="dollar sign" className={styles.dollarSign} src={dollarSign} />,
    amountClass: styles.amountBingo,
  },
  SC: { coin: <SweepCoin alt="sweep coin" className={styles.coin} />, amountClass: styles.amountSc },
  CC: { coin: <SocialCoin alt="social coin" className={styles.coin} />, amountClass: styles.amountCc },
};

export const WinnerTile = memo(({ tile, amountMaxWidth, amountMaxFont, onSelect }: WinnerTileProps) => {
  const { coin, amountClass } = WIN_VARIANTS[tile.isBingo ? 'bingo' : tile.mode];
  const amountText = tile.amount.toLocaleString();
  const fitAmountRef = useFitText(amountMaxFont, 7, amountMaxWidth, amountText);

  return (
    <div
      className={classNames(styles.tile, { [styles.tileBingo]: tile.isBingo, [styles.notClickable]: !onSelect })}
      onClick={onSelect ? () => onSelect(tile.gameId) : undefined}
    >
      {tile.isBingo && <span className={styles.bingoGlow} style={{ backgroundImage: `url(${bingoGlow})` }} />}
      <img
        alt={tile.isBingo ? 'bingo' : 'provider logo'}
        className={`${styles.thumb} ${tile.isBingo ? styles.thumbBingo : ''}`}
        draggable={false}
        loading="lazy"
        src={tile.isBingo ? bingoThumbnail : tile.gameImageUrl || fallbackThumbnail}
      />
      <div className={styles.info}>
        <span className={styles.nickname}>{tile.nickname}</span>
        <span className={styles.amountRow}>
          {coin}
          <span ref={fitAmountRef} className={classNames(styles.amount, amountClass)}>
            {amountText}
          </span>
        </span>
      </div>
    </div>
  );
});

WinnerTile.displayName = 'WinnerTile';
